/**
 * The CPU-side UO hue application for a single RGBA pixel - the software twin of
 * the GPU {@link ./hueFilter} shader, sharing the same pure decode helpers
 * (`decodeUoHue` / `isNearGray`, `src/uoHue.ts`) and the same baked 32-step hue
 * ramp semantics. This is the ONE software recolour path: the card art painter
 * (`src/cardArt.ts`, which re-exports {@link huePixel} as its historical
 * `hueCardPixel` name) and the paperdoll compositor
 * ({@link ./paperdollRenderer}) both apply hues through it, so a hue can never
 * render differently between the two 2D-canvas surfaces or drift from the
 * shader's modes (full hue, partial cloth dye, spectral).
 */
import { decodeUoHue, isNearGray } from '../uoHue';
import type { HuePalette } from './huePalette';

export type RgbaPixel = readonly [number, number, number, number];

/**
 * Apply ClassicUO's encoded body-hue modes to one pixel. `palette` is the active
 * hue's 32-entry gradient row (see {@link paletteRow}); `partial` forces the
 * partial cloth-dye path for garments whose registry definition sets
 * `partialHue` (the wire's `PARTIAL_HUE_FLAG` selects it too, exactly as
 * {@link ../uoHue.decodeUoHue} does for the shader).
 */
export function huePixel(
  source: RgbaPixel,
  encodedHue: number,
  palette: readonly RgbaPixel[],
  partial = false,
): RgbaPixel {
  const [r, g, b, a] = source;
  const mode = decodeUoHue(encodedHue, partial);
  if (mode.spectral) {
    return [0, 0, 0, Math.round(a * Math.max(0, 1 - (r / 255) * 1.5))];
  }

  if (
    mode.partial
    && !isNearGray(r / 255, g / 255, b / 255)
  ) {
    return source;
  }

  const luminance = Math.min(1, Math.max(0, (0.299 * r + 0.587 * g + 0.114 * b) / 255));
  const hued = palette[Math.floor(luminance * 31)];
  return hued ? [hued[0], hued[1], hued[2], a] : source;
}

/**
 * Read one hue's 32-entry gradient row out of the CPU ramp readout
 * ({@link ../render/huePalette.HuePalette}). Hue ids are 1-based (row `hue - 1`,
 * matching the shader's row math); a hue beyond the baked ramp falls back to row
 * 0 (hue 1), the same defensive clamp the card painter and the GPU filter apply.
 * Hue `<= 0` (no hue) yields an empty palette, which leaves {@link huePixel}
 * pass-through.
 */
export function paletteRow(palette: HuePalette, paletteHue: number): RgbaPixel[] {
  if (paletteHue <= 0) return [];
  const row = paletteHue <= palette.height ? paletteHue - 1 : 0;
  const out: RgbaPixel[] = [];
  for (let col = 0; col < palette.width; col++) {
    const i = (row * palette.width + col) * 4;
    out.push([
      palette.data[i] ?? 0,
      palette.data[i + 1] ?? 0,
      palette.data[i + 2] ?? 0,
      palette.data[i + 3] ?? 0,
    ]);
  }
  return out;
}
